/**
 * Constructors for the /v1/sync ops the game queues while it plays (SY-01.3). Every op
 * carries a uuid `opId`; the server runs each at most once, so a retried batch is safe.
 */
import type { BoosterKey } from '../config/content.js';
import type { RoundBody } from './dto.js';
import { uuid } from './ids.js';
import type { OutboxOp, Settings, Style } from './state.js';

export const op = {
  /** A finished round (level mode). `held` rounds wait for their win screen to close. */
  round: (body: RoundBody, held = false): OutboxOp => ({ opId: body.roundId, kind: 'round', body, ...(held ? { held: true } : {}) }),
  upgrade: (shopId: string, slot: string, style: Style): OutboxOp => ({ opId: uuid(), kind: 'upgrade', body: { shopId, slot, style } }),
  /** place a decor piece in a shop's spot (moving it from any other), or clear the spot with null */
  decor: (shopId: string, spot: string, decor: string | null): OutboxOp => ({ opId: uuid(), kind: 'decor', body: { shopId, spot, decor } }),
  shopName: (name: string): OutboxOp => ({ opId: uuid(), kind: 'shopName', body: { name } }),
  dailyGift: (localDate: string): OutboxOp => ({ opId: uuid(), kind: 'dailyGift', body: { localDate } }),
  offlineCollect: (adNonce?: string): OutboxOp => ({ opId: uuid(), kind: 'offlineCollect', body: adNonce ? { adNonce } : {} }),
  buyBooster: (booster: BoosterKey, qty = 1): OutboxOp => ({ opId: uuid(), kind: 'buyBooster', body: { booster, qty } }),
  settings: (patch: Record<string, unknown>): OutboxOp => ({ opId: uuid(), kind: 'settings', body: patch }),
};

/**
 * The body of the `settings` op for a change of the player's settings: the app's `locale` is the
 * server's `language`, every other key keeps its name and none is dropped (the server keeps the keys
 * its Settings schema names: packages/schemas api-r1.ts). Empty when there is nothing to send.
 */
export function settingsBody(patch: Partial<Settings>): Record<string, unknown> {
  const { locale, ...rest } = patch;
  return { ...rest, ...(locale !== undefined ? { language: locale } : {}) };
}

/** The wire shape (local bookkeeping stripped). */
export function wire(o: OutboxOp): Omit<OutboxOp, 'held' | 'resent'> {
  const { held: _h, resent: _r, ...rest } = o;
  return rest;
}
